<template>
  <div>
    <div class="page-heading">
      <h1 class="page-heading__title">
        Booking details
      </h1>
    </div>
    <div class="container">
      <div v-if="booking.product && booking.product.isDynamicBundle" class="mb-4 mt-4 non-cancellable">
        <i class="icon--alert" />
        <p><strong>Booking can not be amended via telephone</strong></p>
        <p>This booking contains a parking bundle which can not be amended via telephone. The customer will need to cancel their booking and rebook through the online Parking flow.</p>
      </div>
      <div class="row mb-4 mt-4">
        <div class="col-md-9">
          <div class="row">
            <div class="col-md-6 mb-4">
              <div class="row">
                <div class="col-12 mb-4">
                  <Card header="Booking Details">
                    <BookingDetail />

                    <ConfirmationEmail
                      v-if="booking.customer"
                      :edit-mode.sync="editModeEnabled"
                    />
                  </Card>
                </div>

                <div class="col-12 mb-4">
                  <Card
                    :edit-mode.sync="customerEditMode"
                    :show-edit-button="userCanEditCustomerDetails"
                    header="Customer Details"
                  >
                    <Customer :edit-mode.sync="customerEditMode" />
                  </Card>
                </div>

                <div class="col-md-12 mb-4">
                  <Card header="Payment Details">
                    <PaymentDetails />
                  </Card>
                </div>

                <div class="col-md-12 mb-4">
                  <Card
                    :edit-mode.sync="vehicleEditMode"
                    :show-edit-button="editModeIncludingThirdParty && userCanEditVehicleBookingDetails"
                    header="Vehicle"
                  >
                    <Vehicle :edit-mode.sync="vehicleEditMode" />
                    <template v-if="booking.product">
                      <div class="col-12 col-md-4">
                        <button
                          v-if="!vehicleEditMode && (new Date(booking.product.entryDate) < new Date()) && (new Date() < new Date(booking.product.exitDate))"
                          class="btn btn-primary btn-block mt-3"
                          @click="purgeRegistration"
                        >
                          Purge Registration
                        </button>
                      </div>
                    </template>
                  </Card>
                </div>
              </div>
            </div>
            <div class="col-md-6 mb-4">
              <div class="row">
                <div class="col-md-12 mb-4">
                  <Card header="Booking History">
                    <History />
                  </Card>
                </div>

                <div class="col-md-12 mb-4">
                  <Card :header="`${booking.notes.length} Notes`">
                    <Notes :edit-mode.sync="editModeIncludingThirdParty" />
                  </Card>
                </div>

                <div class="col-md-12 mb-4">
                  <Card header="Audit">
                    <Audit />
                  </Card>
                </div>

                <div
                  v-if="getFeatureActiveByName('BarrierStatusEnabled')"
                  class="col-md-12 mb-4"
                >
                  <Card header="Barrier Status">
                    <BarrierStatus />
                  </Card>
                </div>

                <div class="col-md-12 mb-4">
                  <Card
                    :edit-mode.sync="flightEditMode"
                    :show-edit-button="editModeEnabled && userCanEditFlightBookingDetails"
                    header="Flight"
                  >
                    <Flight :edit-mode.sync="flightEditMode" />
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-3 sticky-content">
          <div class="row">
            <div class="col-md-12 mb-4">
              <Card
                :edit-mode.sync="productEditMode"
                :show-edit-button="editModeEnabled && userCanEditBookingTimes && bookingType === 1"
                :edit-button-text="'Times'"
                :booking-type="bookingType"
                header="Product summary"
              >
                <button
                  :disabled="!modifiableBooking || !userCanEditBookingProducts || isDynamicBundle"
                  class="btn btn-primary btn-block mt-3"
                  @click="modifyNav"
                >
                  Modify
                </button>
                <Product
                  v-if="bookingType === 3"
                  :edit-mode.sync="productEditMode"
                  first-option-label="Flying Out"
                  second-option-label="Flying In"
                />
                <Product
                  v-else
                  :edit-mode.sync="productEditMode"
                />
                <Refund
                  :disabled="!userCanRefundBookings"
                  :booking-id="bookingId"
                />
                <Cancellation
                  :disabled="!userCanCancelBookings"
                  :booking-id="bookingId"
                />
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import BookingDetail from './BookingDetail'
import Product from './Product'
import PaymentDetails from './PaymentDetails'
import Customer from './Customer'
import ConfirmationEmail from './ConfirmationEmail'
import Notes from './Notes'
import Vehicle from './Vehicle'
import Flight from './Flight'
import History from './History'
import BarrierStatus from './BarrierStatus'
import Audit from './Audit'
import Card from '../../layout/Card'
import Cancellation from './Cancellation'
import Refund from './Refund'

export default {
    name: 'BookingDetailPage',
    components: {
        BookingDetail,
        Product,
        PaymentDetails,
        Customer,
        ConfirmationEmail,
        Notes,
        Vehicle,
        Flight,
        History,
        BarrierStatus,
        Audit,
        Card,
        Cancellation,
        Refund
    },
    data () {
        return {
            vehicleEditMode: false,
            customerEditMode: false,
            flightEditMode: false,
            productEditMode: false,
            cancelInProgress: false,
            bookingId: this.$route.params.bookingId
        }
    },
    computed: {
        ...mapState({
            booking: state => state.booking,
            bookingDetail: state => state.booking.bookingDetail,
            bookingType: state => state.booking.bookingType,
            paymentDetail: state => state.booking.paymentDetail
        }),
        ...mapGetters({
            userCanEditBookingProducts: 'auth/userCanEditBookingProducts',
            userCanCancelBookings: 'auth/userCanCancelBookings',
            userCanRefundBookings: 'auth/userCanRefundBookings',
            userCanEditVehicleBookingDetails: 'auth/userCanEditVehicleBookingDetails',
            userCanEditFlightBookingDetails: 'auth/userCanEditFlightBookingDetails',
            userCanEditBookingTimes: 'auth/userCanEditBookingTimes',
            userCanEditCustomerDetails: 'auth/userCanEditCustomerDetails',
            getFeatureActiveByName: 'feature/getFeatureActiveByName' }),
        editModeEnabled () {
            if (this.$store.state.auth.loggedIn) {
                if (this.$store.state.feature.features.length === 0) { this.setFeatures() }
                return this.$store.getters['feature/getFeatureActiveByName']('EditBookings') &&
                    this.modifiableBooking
            }
            return false
        },
        editModeIncludingThirdParty () {
            if (this.$store.state.auth.loggedIn) {
                if (this.$store.state.feature.features.length === 0) { this.setFeatures() }
                return this.$store.getters['feature/getFeatureActiveByName']('EditBookings') &&
                    this.modifyThirdPartyBooking
            }
            return false
        },

        modifyThirdPartyBooking () {
            const bookingDetail = this.$store.state.booking.bookingDetail
            const product = this.$store.state.booking.product
            if (this.paymentDetail !== null && product != null) {
                return (bookingDetail.status.toLowerCase() === 'confirmed' &&
                    new Date(product.entryDate) > new Date())
            }
            return false
        },
        modifiableBooking () {
            const bookingDetail = this.$store.state.booking.bookingDetail
            const product = this.$store.state.booking.product
            if (bookingDetail !== null && this.paymentDetail !== null && product != null) {
                return (bookingDetail.status.toLowerCase() === 'confirmed' &&
                    new Date(product.entryDate) > new Date()) &&
                    bookingDetail.channelTypeID !== 8
            }
            return false
        },
        isDynamicBundle () {
            return this.$store.state.booking.product.isDynamicBundle
        }
    },
    created: function () {
        this.$store.dispatch('clearBookingData')
        this.$store.dispatch('getBookingType', this.bookingId)
    },
    methods: {
        modifyNav () {
            switch (this.bookingType) {
                case 1:
                    this.$router.push('/booking/' + this.bookingId + '/modify')
                    break
                case 2:
                    this.$router.push('/booking/' + this.bookingId + '/modify/lounge')
                    break
                case 3:
                    this.$router.push('/booking/' + this.bookingId + '/modify/fasttrack')
                    break
            }
        },
        purgeRegistration () {
            this.$store.dispatch('updateVehicle', [
                this.$route.params.bookingId,
                {
                    ...this.vehicleEdit,
                    registration: this.$store.state.booking.bookingDetail.bookingReference.substring(this.$store.state.booking.bookingDetail.bookingReference.length - 6, this.$store.state.booking.bookingDetail.bookingReference.length)
                }
            ]).then((response) => {
                this.$toasted.success('Vehicle details saved')
                this.$store.dispatch('getAudit', this.$route.params.bookingId)
                this.editModeValue = false
                this.noVehicleFound = false
                this.$store.dispatch('getVehicle', this.$route.params.bookingId)
            }).catch((response) => {
                this.$toasted.error(`Update Failed. ${response.data}`)
            })
        },
        async setFeatures () {
            await this.$store.dispatch('feature/setFeatures')
        }
    }
}
</script>
